import React, { useEffect, useMemo, useRef, useState } from "react";
import { StyleObject } from "styletron-react";
import { colors, fonts, frames, glyphs, icons } from "../../Styles";
import { CoreNavLink, MainContentPaddingTypes, NewButton, NewInput, NewModal, NewSelect } from "../../Components";
import { ButtonTypes, NavLinkTypes, NewInputHandles, NewSelectHandles, systemTypeOptions } from "../../Definitions";
import { useAppSelector } from "../../Store";
import { Graph_QL, useSystem } from "../../Services";
import { random } from "lodash";
import { SystemMenu_Pages, focus_viewMenu_Pages } from "../pages";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { NavigationFunction as RootNavigation, } from "../../Navigation/root";
import { selectAllSystemLimitations } from "../../Services/Agrisys_GqlDataCaller/SystemCalls/systemLimitationsSlice";

interface SystemMenu_Props {
  focus_view?: boolean;
  overrides?: {
    gridArea?: string;
  };
}

export const systemTypeToLabel: Record<number, string> = {
  1: "Freeda Location Feeder",
  2: "Freeda ESF Walk Through",
};

export default function SystemMenu({ focus_view, overrides }: SystemMenu_Props) {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const inputRef = useRef<NewInputHandles>(null);
  const selectRef = useRef<NewSelectHandles>(null);

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const [createSystemModal, setCreateSystemModal] = React.useState<boolean>(false);
  const [message, setMessage] = useState<string | undefined>(undefined)

  const { call: getSystemsInfo, data: systemsState, createSystem, getSystemLimitationsInfo } = useSystem(connection);
  const stateLimits = useAppSelector(selectAllSystemLimitations);

  useEffect(() => {
    getSystemLimitationsInfo(domain)
    getSystemsInfo({}, connection);
  }, [connection, domain]);

  const _Root = useMemo<StyleObject>(() => {
    return Object.assign(
      {} as StyleObject,
      overrides && overrides.gridArea ? { gridArea: overrides.gridArea } : {}
    );
  }, [overrides]);

  // TODO: Fjern Weaner Unit og Sow Unit når systemtype 2 og 3 eksisterer i db
  const systems = useMemo(() => {
    const original = (systemsState && systemsState[domain]?.data) || [];
  
    return [...original];
  }, [domain, systemsState]);

  const systemTypeToIcon: Record<number, string> = {
    1: icons.freedaLF,
    2: icons.freedaESF_WalkThrough
  };
  
  const systemTypeToPage: Record<number, SystemMenu_Pages | focus_viewMenu_Pages> = {
    1: focus_view === true ? focus_viewMenu_Pages.op_freeda_lf : SystemMenu_Pages.freeda_lf,
    2: SystemMenu_Pages.freeda_esf
  };

  const systemLimitsByType = useMemo(() => {
    const map: Record<number, { limit: number; used: number }> = {};
  
    if (!stateLimits) return map;
  
    for (const l of stateLimits) {
      const used = systems.filter(s => s.fk_system_types === l.fk_system_types).length;
      map[l.fk_system_types] = {
        limit: l.systems_limit,
        used
      };
    }
  
    return map;
  }, [stateLimits, systems]);

  const systemTypeOptionsWithLimit = useMemo(() => {
    return systemTypeOptions
      .filter(opt => opt.id !== 2) // OBS ESF DEACTIVATED
      .map(opt => {
        console.log(systemTypeOptions)
        const data = systemLimitsByType[opt.id];
        const used = data?.used ?? 0;
        const limit = data?.limit ?? 0;
    
        return {
          ...opt,
          secondaryLabel: `(${used}/${limit})`
        };
      });
  }, [systemLimitsByType, systemTypeOptions]);

  const handleCreate = async () => {
    const newSystem: { name?: string; fk_system_types?: number } = {};

    if (inputRef && inputRef.current && inputRef.current) {
      newSystem.name = inputRef.current.value.toString();
    }

    if (selectRef && selectRef.current && selectRef.current) {
      newSystem.fk_system_types = selectRef.current.value.id ? +selectRef.current.value.id : undefined;
    }

    if (!newSystem.name) {
      setMessage(t("systemControls.createSystem.systemNameMissing", { defaultValue: "system name missing" }));
      setTimeout(() => setMessage(""), 2000);
      return;
    }
    
    if (!newSystem.fk_system_types) {
      setMessage(t("systemControls.createSystem.systemTypeMissing", { defaultValue: "system type missing" }));
      setTimeout(() => setMessage(""), 2000);
      return;
    }

    const limitData = systemLimitsByType[newSystem.fk_system_types];
    if (limitData && limitData.used >= limitData.limit) {
      setMessage(`${t("systemControls.createSystem.systemLimitReached", { defaultValue: "system limit reached" })} (${limitData.used}/${limitData.limit})`);
      setTimeout(() => setMessage(""), 2000);
      return;
    }

    const response = await createSystem(domain, newSystem.fk_system_types, newSystem.name);
    if (response.success) {
      setCreateSystemModal(false);
      RootNavigation({ Navigator: navigator, navigation_page: systemTypeToPage[newSystem.fk_system_types], navigation_tab: systemTypeToPage[newSystem.fk_system_types], replace: true, navArgs: { selected: response.system_id }, currentDomain: farm })
    }
  };

  return (
    <frames.SystemMenu $style={_Root}>
      {!systems ? (
        <p style={{ ...fonts.fontsize4, ...fonts.paragraph, color: colors.greyLightTertiary, textAlign: "center" }}>No Systems Found</p>) : ( // TODO Translate
          <frames.SystemList $focusView={focus_view ?? false}>
          {systems.map(system => {
            const page = systemTypeToPage[system?.fk_system_types];
            const systemId = system.system_id;
            const systemName = system.name;
            const iconSrc = systemTypeToIcon[system.fk_system_types];
            const toPath = `/${page}/${systemId}`;

            return (
              <CoreNavLink
                key={"page_"+systemId+random(0, 9999)}
                content={systemName}
                subContent={systemTypeToLabel[system.fk_system_types]}
                navLinkType={NavLinkTypes.systemNavLink}
                path={toPath}
                endPath={false}
                icon={iconSrc}
                focus_view={focus_view ?? false}
                overrides={{ }}
              />
            );
          })}
        </frames.SystemList>
      )}
      <div style={{borderTop: "2px solid " + colors.greyDarkSecondary, margin: "0px 10px", boxSizing: "border-box"}}/>
      <div style={{ width: "100%", display: "flex", boxSizing: "border-box", alignItems: "flex-end", justifyContent: "flex-start", paddingLeft: "20px" }}>
        <NewButton
          content=""
          startEnhancer={glyphs.Add({})}
          buttonType={ButtonTypes.glyphButton}
          overrides={{ baseStyles: { width: "fit-content", height: "fit-content" } }}
          onClick={() => setCreateSystemModal(true)}
        />
      </div>

      {createSystemModal && 
        <NewModal
          header={t("systemControls.createSystem.createNewSystem", { defaultValue: "create new system" })}
          predefinedButtons={{
            onClose: () => setCreateSystemModal(false)
          }}
          overrides={{
            mainContentPadding: MainContentPaddingTypes.secondary
          }}
          mainContent={
            <div style={{ display: "flex", flexDirection: "column", gap: "20px", alignItems: "center" }}>
              <NewInput
                ref={inputRef}
                header={t("systemControls.createSystem.systemName", { defaultValue: "system name" })}
                isRequired
              />
              <NewSelect
                ref={selectRef}
                header={t("systemControls.createSystem.systemType", { defaultValue: "system type" })}
                options={systemTypeOptionsWithLimit}
                isRequired
              />
            </div>
          }
        footerContent={
          <NewButton content={t("systemControls.createSystem.createSystem", { defaultValue: "create system" })} onClick={handleCreate} />}
          footerMessage={message}
        />
      }
    </frames.SystemMenu>
  );
}